import { MenuItem, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  getMaxEndDate,
  getMinStartDate,
  isDateRangeInvalid,
} from "../constants";

interface AnalyticsFilterItemsProps {
  data: LeadsManagementData;
  draftBrand: string;
  draftStart: string;
  draftEnd: string;
  onBrandChange: (value: string) => void;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
}

function toDayjs(value: string): Dayjs | null {
  return value ? dayjs(value) : null;
}

function toIsoDate(value: Dayjs | null): string {
  return value && value.isValid() ? value.format("YYYY-MM-DD") : "";
}

// DatePicker's own `readOnly` prop disables the popup calendar too, which
// isn't what we want — only keyboard entry should be blocked, selecting via
// the calendar must keep working. Tab is left alone so focus can still move.
function blockTyping(event: React.KeyboardEvent) {
  if (event.key !== "Tab") event.preventDefault();
}

function blockPaste(event: React.ClipboardEvent) {
  event.preventDefault();
}

export default function AnalyticsFilterItems({
  data,
  draftBrand,
  draftStart,
  draftEnd,
  onBrandChange,
  onStartChange,
  onEndChange,
}: AnalyticsFilterItemsProps) {
  const { brandOptions } = data;
  const today = dayjs();
  // getMaxEndDate only caps the 365-day range window — it says nothing about
  // "today", so without this the End date picker let you pick a date in the
  // future. Whichever is earlier (the range cap or today) wins.
  const rangeMaxEndDate = toDayjs(getMaxEndDate(draftStart) ?? "");
  const maxEndDate = rangeMaxEndDate && rangeMaxEndDate.isBefore(today) ? rangeMaxEndDate : today;
  const minStartDate = getMinStartDate(draftEnd);

  // A change that would push the range over 365 days (or invert it) is
  // rejected outright — the other date must be adjusted first, it is never
  // auto-adjusted for the user. Typing is blocked on both pickers (see
  // blockTyping/blockPaste below) so this mainly guards picker selections,
  // but it stays as a defensive backstop.
  function handleStartChange(nextStart: string) {
    if (nextStart && isDateRangeInvalid(nextStart, draftEnd)) return;
    onStartChange(nextStart);
  }

  function handleEndChange(nextEnd: string) {
    if (nextEnd && isDateRangeInvalid(draftStart, nextEnd)) return;
    if (nextEnd && dayjs(nextEnd).isAfter(today, "day")) return;
    onEndChange(nextEnd);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TextField
        select
        label="Select Brand"
        value={draftBrand}
        onChange={(e) => onBrandChange(e.target.value)}
        size="small"
        color="secondary"
        data-tracker="931"
        SelectProps={{ MenuProps: { disablePortal: true } }}
        sx={{ minWidth: 230 }}
      >
        {brandOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <DatePicker
        label="Start date"
        format="DD/MM/YYYY"
        value={toDayjs(draftStart)}
        onChange={(value) => handleStartChange(toIsoDate(value))}
        minDate={toDayjs(minStartDate ?? "") ?? undefined}
        maxDate={toDayjs(draftEnd) ?? today}
        slotProps={{
          textField: {
            size: "small",
            color: "secondary",
            onKeyDown: blockTyping,
            onPaste: blockPaste,
            "data-tracker": "932",
          } as any,
          popper: { disablePortal: true },
        }}
      />

      <DatePicker
        label="End date"
        format="DD/MM/YYYY"
        value={toDayjs(draftEnd)}
        onChange={(value) => handleEndChange(toIsoDate(value))}
        minDate={toDayjs(draftStart) ?? undefined}
        maxDate={maxEndDate}
        slotProps={{
          textField: {
            size: "small",
            color: "secondary",
            onKeyDown: blockTyping,
            onPaste: blockPaste,
            "data-tracker": "933",
          } as any,
          popper: { disablePortal: true },
        }}
      />
    </LocalizationProvider>
  );
}
